<template>
  <div>
    <div class="md:min-h-[calc(100dvh-70px)] pb-14 pt-10 maxXs:pb-20 flex justify-center items-center relative z-10">
      <div class="verification-wrapper bg-gray-200 flex">
        <div class=" code-wrapper flex flex-col justify-evenly bg-white rounded-lg m-auto md:mt-auto mt-20 w-[96%] md:w-[580px] px-[15px] md:px-8 py-8 md:py-[48px] shadow-ts1">
          <p v-if="!hasError && !codeExpired && attemptsLeft" class="text-center font-display text-[30px] md:text-[40px] text-blue mb-6 md:mb-[55px] leading-[62px] font-bold">
            {{ $t('verify_your_email') }}
          </p>
          <p
            v-else-if="codeExpired && attemptsLeft"
            @click="requestNewCode"
            class="text-center font-display text-[30px] md:text-[40px] text-orange mb-[50px] leading-[62px] font-bold"
          >
            {{ $t('resend_code') }}
          </p>
          <p v-else-if="!attemptsLeft" class="text-center font-display text-[30px] md:text-[40px] text-orange mb-[50px] leading-[62px] font-bold">
            {{ $t('verification_failed') }}
          </p>
          <p v-else class="text-center font-display text-[30px] md:text-[40px] text-orange mb-[50px] leading-[62px] font-bold">{{ $t('try_again') }}</p>

          <div :class="{ 'border-4 border-orange border-solid pt-[45px] px-3 md:px-0': hasError }">
            <p v-if="!hasError && !codeExpired && attemptsLeft" class="text-center font-body text-[16px] leading-7 px-5 md:px-[60px] text-gray-900">
              {{ $t('verify_your_email_description') }}
            </p>
            <p v-else-if="codeExpired" class="text-center font-body text-[16px] leading-7 md:px-[60px] text-gray-900">{{ $t('try_again_expired') }}</p>
            <div v-else-if="!attemptsLeft" class="text-center font-body text-[16px] leading-7 md:px-[60px] text-gray-900">
              <p class="text-center font-body text-[16px] leading-7 md:px-[60px] text-gray-900 font-semibold">{{ $t('verification_failed_header') }}</p>
              <p class="text-center font-body text-[16px] leading-7 md:px-[60px] text-gray-900">{{ $t('verification_failed_description') }}</p>
            </div>
            <div v-else>
              <p class="text-center font-body text-[16px] leading-7 md:px-[60px] text-gray-900 font-semibold">{{ $t('try_again') }}</p>
              <p class="text-center font-body text-[16px] leading-7 md:px-[60px] text-gray-900">{{ $t('try_again_description') }}</p>
            </div>

            <div v-if="countDown" class="w-full mt-[45px]">
              <span class="leading-4 flex align-center text-center flex-col font-display text-green">
                <span class="text-[35px] font-bold">{{ timerCount }}</span>
                <span class="text-base">sec</span>
              </span>
            </div>

            <div class="flex flex-col" v-if="!codeExpired && attemptsLeft">
              <div class="flex gap-2 justify-center md:px-[60px] pb-[21px] pt-[35px] md:pt-[44px] w-fit m-auto" :class="{ 'md:pt-[25px]': countDown }">
                <div v-for="(digit, index) in verificationCode" :key="index" class="verification-box">
                  <input
                    class="w-[42px] h-[50px] text-center"
                    :class="{ 'border-solid border-orange': hasError }"
                    ref="inputs"
                    type="text"
                    v-model="verificationCode[index]"
                    maxlength="1"
                    @input="handleInputChange(index)"
                    @keyup="handleKeyDown($event, index)"
                    @focus="handleFocus($event.target)"
                    @paste="handlePaste"
                  />
                </div>
              </div>
              <div v-if="hasError && !codeExpired && attemptsLeft" class="text-center">{{ `${isEmailVerified.attempts}  ${$t('attempts_left')}` }}</div>
              <div class="flex justify-between w-[292px] m-auto md:w-full md:px-[112px] pb-[44px] md:pb-[58px]">
                <p v-if="!hasError" class="w-1/2 text-[12px] text-gray-900">{{ $t('code_not_recieved') }}</p>
                <button
                  v-if="!hasError && attemptsLeft"
                  @click="requestNewCode"
                  class="w-1/2 text-[12px] ltr:text-right rtl:text-left text-green p-0"
                  :class="{ 'pointer-events-none': countDown }"
                >
                  {{ $t('resend') }}
                </button>
              </div>
            </div>
          </div>

          <div class="clock flex justify-center py-8" v-if="codeExpired && !hasError && attemptsLeft">
            <img src="../../assets/img/expired.png" width="71" height="71" />
          </div>
          <div class="button-wrapper text-center mt-10">
            <Button v-if="!codeExpired && attemptsLeft" @click="verifyEmail" customClasses="!w-auto mb-[10px] px-[24px]">
              {{ $t('confirm_email') }}
            </Button>
            <Button v-else-if="!codeExpired && !attemptsLeft" customClasses="!w-auto mb-[10px] px-[24px]">
              <a :href="contactURL">
                {{ $t('contact_us') }}
              </a>
            </Button>
            <Button v-else-if="codeExpired && attemptsLeft" mode="primary-outlined" @click="requestNewCode" customClasses="!w-auto mb-[10px] px-[24px]">
              {{ $t('resend_code') }}
            </Button>
            <p class="text-gray-900" v-if="attemptsLeft">
              {{ $t('need_help') }}
              <a :href="contactURL"
                ><span class="underline">{{ $t('contact_us') }}</span></a
              >
            </p>
          </div>
        </div>
      </div>
    </div>
    <logo-loader v-if="logoLoaderActive && ongoingCalls > 0"></logo-loader>
  </div>
</template>

<script>
import { service } from '../../main.js'
import { mapGetters, mapActions } from 'vuex'
import links from '@/config/links.js'
import LogoLoader from '@/components/LogoLoader.vue'
import Button from '@/components/reusableComponents/Button.vue'
import config from '../../config'

export default {
  components: { LogoLoader, Button },
  data() {
    return {
      activeMessage: '',
      verificationCode: ['', '', '', '', '', ''],
      hasError: false,
      expired: false,
      redirectToHome: false,
      countDown: false,
      timerCount: 60,
    }
  },
  computed: {
    codeExpired: {
      get() {
        return this.isEmailVerified.attempts > 0 && new Date(new Date(this.isEmailVerified.code_issued_at).getTime() + 60 * 60 * 24 * 1000) <= new Date()
      },
      set(newValue) {
        this.expired = newValue
      },
    },
    attemptsLeft() {
      if (this.isEmailVerified) return this.isEmailVerified.attempts > 0
      return true
    },
    contactURL() {
      return links.getLink('contact', 'en')
    },
    ...mapGetters({
      Language: 'language/code',
      User: 'user/all',
      isEmailVerified: 'user/isEmailVerified',
      ongoingCalls: 'logoLoader/ongoingCalls',
      logoLoaderActive: 'logoLoader/active',
    }),
  },
  created() {
    this.codeExpired = this.isEmailVerified.attempts > 0 && new Date(new Date(this.isEmailVerified.code_issued_at).getTime() + 60 * 60 * 24 * 1000) <= new Date()
  },
  mounted() {
    this.$bus.$emit('ChangeHeadings', this.$t('verify_your_email') + ' | ' + this.$t('app_name'))
    this.addMeta('description', this.$t('verify_email_description'))
    this.addMeta('canonical', config.URL.cp + this.$route.path)
    this.addMeta('alternate', this.$route.meta.identifierPath)
    this.addMeta('ogtitle', this.$t('verify_your_email'))

    this.$nextTick(() => {
      if (Array.isArray(this.$refs.inputs) && this.$refs.inputs.length > 0) {
        if (this.Language === 'fa' && this.$refs.inputs[5]) {
          this.$refs.inputs[5].focus()
        } else if (this.$refs.inputs[0]) {
          this.$refs.inputs[0].focus()
        }
      }
    })
  },
  watch: {
    /**
     * Watch for countdown finish. Reset it and ready for next countdown.
     */
    timerCount() {
      if (this.timerCount === 0) {
        this.timerCount = 60
        this.countDown = false
      }
    },
    /**
     * Watch for countDown if true start the countDown.
     */
    countDown() {
      if (this.countDown) this.countDownTimer()
    },
    '$i18n.locale'(newVal, oldVal) {
      this.$validator.reset()
      if (newVal === 'fa' || oldVal === 'fa') this.verificationCode.reverse()
    },
    isEmailVerified: {
      immediate: true,
      deep: true,
      handler(u) {
        if (u.verified === true) {
          return this.$router.push('/' + this.Language + '/')
        }
      },
    },
  },
  methods: {
    verifyEmail() {
      this.hasError = false
      const isRTL = this.Language === 'fa'
      const processedDigits = [...this.verificationCode]
      isRTL ? processedDigits.reverse() : processedDigits
      const code = processedDigits.toString().replaceAll(',', '')
      if (code === '') return (this.hasError = true)
      if (code.length < 6) return (this.hasError = true)
      // this['logoLoader/set'](true)
      service
        .post('/email/verify', {
          email: this.User.info.email,
          verification_code: code,
        })
        .then(response => {
          // this['logoLoader/set'](false)
          if (response.data.error === 'expired') return (this.codeExpired = true)
          if (response.data.error === 'wrong_code') {
            this.$store.commit('user/SET_EMAIL_VERIFIED', {
              attempts: this.isEmailVerified.attempts - 1,
              code_issued_at: this.isEmailVerified.code_issued_at,
              verified: this.isEmailVerified.verified,
            })
            return (this.hasError = true)
          }
          if (response.data.verified) {
            this.$store.commit('user/SET_EMAIL_VERIFIED', {
              attempts: this.isEmailVerified.attempts,
              code_issued_at: this.isEmailVerified.code_issued_at,
              verified: response.data.verified,
            })
            return this.$router.push('/' + this.Language + '/')
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    requestNewCode() {
      // this['logoLoader/set'](true)
      service
        .post('/email/request-email-verification', {
          email: this.User.info.email,
        })
        .then(() => {
          // this['logoLoader/set'](false)
          this.codeExpired = false
          this.hasError = false
          this.$store.commit('user/SET_EMAIL_VERIFIED', {
            attempts: this.isEmailVerified.attempts,
            code_issued_at: new Date(),
            verified: this.isEmailVerified.verified,
          })
          // Start countDown timer
          this.countDown = true
        })
        .catch(error => {
          console.log(error)
        })
    },
    handleInputChange(index) {
      // Remove any non-numeric characters
      this.verificationCode[index] = this.verificationCode[index].replace(/\D/g, '');

      if (/^[0-9]$/.test(this.verificationCode[index])) {
        const isRTL = this.Language === 'fa'
        if (isRTL) {
          // If RTL, focus the previous input field (if it exists)
          if (index > 0) {
            this.$refs.inputs[index - 1].focus()
          }
        } else {
          // If LTR, focus the next input field (if it exists)
          if (index < this.verificationCode.length - 1) {
            this.$refs.inputs[index + 1].focus()
          }
        }
      }
    },
    handlePaste(event) {
      event.preventDefault()
      const pasteData = event.clipboardData.getData('text/plain').trim()
      if (pasteData.length > 6) return
      const isRTL = this.Language === 'fa'
      const digits = pasteData.split('').slice(0, 6)
      const processedDigits = [...digits]
      isRTL ? processedDigits.reverse() : processedDigits
      processedDigits.forEach((digit, index) => {
        if (/^[0-9]$/.test(digit)) {
          this.$refs.inputs[index].value = digit
          this.verificationCode[index] = digit
        }
      })
      this.$refs.inputs[5].focus()
    },
    handleKeyDown(e, index) {
      if (!/^[0-9]{1}$/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && !e.metaKey) {
        e.preventDefault()
      }
      // RTL Detection: Check if verificationCode contains RTL characters (Arabic/Hebrew)
      const isRTL = this.Language === 'fa'
      // Handle Delete or Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        let indexToRemove = index
        // In LTR, backspace/delete moves to the left, in RTL, it moves to the right
        if (isRTL) {
          if (index < this.verificationCode.length - 1 && this.verificationCode[index] == '') {
            this.verificationCode.splice(indexToRemove, 1, '')
            indexToRemove = index + 1
          }
        } else {
          if (index > 0 && this.verificationCode[index] == '') {
            this.verificationCode.splice(indexToRemove, 1, '')
            indexToRemove = index - 1
          }
        }
        // Focus the correct input
        this.$refs.inputs[indexToRemove].focus()
      }
    },
    handleFocus(target) {
      target.select()
    },
    /**
     * Function to start the countdown timer
     */
    countDownTimer() {
      if (this.timerCount > 0) {
        setTimeout(() => {
          this.timerCount -= 1
          this.countDownTimer()
        }, 1000)
      }
    },
    ...mapActions(['logoLoader/set']),
  },
}
</script>
<style scoped>
.verification-box {
  display: inline-block;
}
.verification-box input {
  padding: 0;
}
/* Override browser default text selection styling */
.verification-box input::selection {
  background: transparent;
}
.verification-box input::-moz-selection {
  background: transparent;
}
.hasError {
  border: 1px solid;
}
</style>
